import translation from 'locales/translation';
import {
  AgencyIcon,
  ButtonThemes,
  Checkbox,
  CheckCircleIcon,
  Col,
  Collapse,
  ControlLabel,
  EditIcon,
  Etiqueta,
  EtiquetasStyle,
  Input,
  Modal,
  ModalSizes,
  NotebookIcon,
  RadioButton,
  RadioButtonType,
  RefreshIcon,
  Row,
  SearchIcon,
  ThreeDotsLoader,
  ToastTypes,
  UserIcon,
} from 'plataforma-fundacao-componentes';
import {
  ChangeEvent,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import { CircleTimes } from 'components/Icons';
import LayoutContext from 'components/Layout/LayoutContext';
import { ModalProps } from 'plataforma-fundacao-componentes/dist/components/modal/Modal';
import { CSSTransition } from 'react-transition-group';
import {
  CONFIRM_PRESENCE_ALL_PRESENT,
  CONFIRM_PRESENCE_BY_CPF,
  CONFIRM_PRESENCE_CHANGE_INPUT,
  CONFIRM_PRESENCE_EVENT_ID,
  CONFIRM_PRESENCE_LIST,
  CONFIRM_PRESENCE_SET,
  CONFIRM_PRESENCE_SET_METADADOS,
  loadAccount,
} from 'store/modules/ConfirmarPresencaActions';
import ConfirmarPresencaReducer, {
  INITIAL_STATE,
} from 'store/modules/ConfirmarPresencaReducer';
import { formatCPF, formatCPFOrCNPJ } from 'helpers/utils/cpfCnpjMask';
import {
  ConfirmarPresencaModalValidator,
  ValidatorProps,
} from './ConfirmarPresencaValidator';

import CameraIcon from 'components/Icons/CameraIcon';
import {
  AssemblyModalitiesNumber,
  ModalityType,
} from 'helpers/enums/Assembleia';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Webcam from 'react-webcam';
import { postRegisterPresence } from 'services/assessor';
import { IReducer } from 'store/Reducer';
import { isMobile } from 'helpers/utils/HTMLUtils';
import { languageValues } from 'helpers/utils/languageUtils';
import './styles.scss';

const rootClassName = 'componente-confirmar-presenca-modal';

interface IProps extends ModalProps {
  onConfirm: () => Promise<void>;
  eventId?: string | number;
  eventMode?: AssemblyModalitiesNumber;
}

type AccountListProps = {
  codigoAgencia: string;
  codigoCooperativa: string;
  cpfCnpj: string;
  cpfRepresentanteConfirmado: any;
  cpfRepresentanteLegal: any;
  idAssociado: number;
  idConvocado: number;
  modoPresenca: any;
  nomeAssociado: string;
  nomeRepresentanteLegal: any;
  numeroConta: string;
  numeroNucleo: string;
  presente: boolean;
  tipoPresenca: any;
  representantes: {
    cpf: string;
    nome: string;
  }[];
};

enum SignatureTypes {
  FISICA = 'FISICA',
  FACIAL = 'FACIAL',
}

export const ConfirmarPresenca = (props: IProps) => {
  const [sent, setSent] = useState<boolean>(false);
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const [selectAll, setSelectAll] = useState<boolean>(true);
  const [allPresent, setAllPresent] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [representative, setRepresentative] = useState<{
    cpf: string;
    nome: string;
  }>();
  const [expand, setExpand] = useState<number | null>();
  const [allowGeolocation, setAllowGeolocation] = useState<boolean | undefined>(
    undefined
  );
  const [loadCam, setLoadCam] = useState<boolean>(true);
  const [signatureType, setSignatureType] = useState<any>();
  const [showCam, setShowCam] = useState<boolean>(false);
  const [errorWithoutAccount, setErrorWithoutAccount] =
    useState<boolean>(false);
  const [selected, setSelected] = useState<AccountListProps[]>([]);
  const [validator, setValidator] = useState<ValidatorProps>();

  const { showToast } = useContext(LayoutContext);
  const [state, dispatch] = useReducer(ConfirmarPresencaReducer, INITIAL_STATE);

  const { evento } = useSelector(
    (state: IReducer) => state.assembleiasAssessor
  );

  const webcamRef = useRef<any>(null);

  useEffect(() => {
    setValidator(ConfirmarPresencaModalValidator(state.filter.value));
  }, [state.filter.value, allPresent]);

  const handleClose = () => {
    props.onClose?.(props.modalKey);
  };

  function is_numeric(str: string) {
    return /^\d+$/.test(str.replace(/[^\w\s]/gi, ''));
  }

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setError(false);
    setAllPresent(false);

    dispatch({
      type: CONFIRM_PRESENCE_CHANGE_INPUT,
      payload: {
        value: is_numeric(e.target.value)
          ? formatCPF(e.target.value)
          : e.target.value,
        isSearchByCpf: is_numeric(e.target.value) ? true : false,
      },
    });
  }, []);

  useEffect(() => {
    dispatch({
      type: CONFIRM_PRESENCE_EVENT_ID,
      payload: props.eventId,
    });
  }, [props.eventId]);

  useEffect(() => {
    if (state.filter.value) {
      setError(false);

      const delayDebounceFn = setTimeout(() => {
        if (
          !validator ||
          (validator?.nameIsValid().error! && validator?.CpfIsValid().error!) ||
          allPresent
        ) {
          setError(true);
          return;
        }

        loadAccount(showToast, state, dispatch)
          .then(() => {
            dispatch({
              type: CONFIRM_PRESENCE_BY_CPF,
              payload: state.filter.isSearchByCpf,
            });
            dispatch({
              type: CONFIRM_PRESENCE_SET,
              payload: [],
            });
            dispatch({
              type: CONFIRM_PRESENCE_SET_METADADOS,
              payload: [],
            });
            setSignatureType(undefined);
          })
          .finally(() => {
            if (error && errorWithoutAccount) {
              setErrorWithoutAccount(false);
            } else if (!error && !state.accountList.length) {
              setErrorWithoutAccount(true);
            }
          });
      }, 800);

      return () => clearTimeout(delayDebounceFn);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filter.value, validator]);

  useEffect(() => {
    dispatch({
      type: CONFIRM_PRESENCE_SET,
      payload: [],
    });

    if (!state.isSearchByCpf && state.accountList.length === 1) {
      dispatch({
        type: CONFIRM_PRESENCE_SET,
        payload: state.accountList,
      });
    }

    if (state.isSearchByCpf) {
      setSelectAll(true);
      setSelected(
        state.accountList
          .map((item: AccountListProps) => item)
          .filter((x: AccountListProps) => !x.presente)
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.accountList, state.isSearchByCpf]);

  useEffect(() => {
    dispatch({
      type: CONFIRM_PRESENCE_SET,
      payload: selected,
    });
    setRepresentative(undefined);
  }, [selected]);

  useEffect(() => {
    if (
      state.accountList.length &&
      state.accountList.every((x: AccountListProps) => x.presente === true)
    ) {
      setAllPresent(true);
      dispatch({
        type: CONFIRM_PRESENCE_ALL_PRESENT,
        payload: true,
      });
    } else {
      setAllPresent(false);
      dispatch({
        type: CONFIRM_PRESENCE_ALL_PRESENT,
        payload: false,
      });
    }
  }, [state.accountList]);

  const getModalityType = (presentationMode: number) => {
    switch (presentationMode) {
      case ModalityType.ONLINE:
        return <NotebookIcon />;
      case ModalityType.PRESENCIAL:
        return <AgencyIcon />;
      case ModalityType.AGENCIA:
        return <AgencyIcon />;
      default:
        return <AgencyIcon />;
    }
  };

  const getModalityDescription = (presentationDescription: number) => {
    switch (presentationDescription) {
      case ModalityType.ONLINE:
        return 'Digital';
      case ModalityType.AGENCIA:
        return 'Agência';
      case ModalityType.PRESENCIAL:
        return 'Presencial';
      default:
        return 'Presencial';
    }
  };

  const handleSelectAll = (val: boolean) => {
    if (val) {
      setSelected(
        state.accountList
          .map((item: AccountListProps) => item)
          .filter((x: AccountListProps) => !x.presente)
      );
    } else {
      setSelected([]);
    }
  };

  const FACING_MODE_USER = 'user';
  const FACING_MODE_ENVIRONMENT = 'environment';

  const [facingMode, setFacingMode] = useState<string>(FACING_MODE_USER);

  const videoConstraints = {
    facingMode: FACING_MODE_USER,
  };

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const [devices, setDevices] = useState([]);

  const handleDevices = useCallback(
    (mediaDevices: any) =>
      setDevices(mediaDevices.filter(({ kind }: any) => kind === 'videoinput')),
    [setDevices]
  );

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(handleDevices);
  }, [handleDevices]);

  const handleSwitchCam = useCallback(() => {
    setFacingMode((prevState) =>
      prevState === FACING_MODE_USER
        ? FACING_MODE_ENVIRONMENT
        : FACING_MODE_USER
    );
  }, []);

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    const regex = /^.+,/;
    dispatch({
      type: CONFIRM_PRESENCE_SET_METADADOS,
      payload: {
        imagem: imageSrc.replace(regex, ''),
      },
    });
  }, []);

  const handleUserMedia = () => setTimeout(() => setLoadCam(false), 1000);

  const date = String(new Date());

  useEffect(() => {
    if (signatureType === SignatureTypes.FACIAL) {
      navigator.geolocation.getCurrentPosition(function (position: {
        coords: { [key: string]: any };
        timestamp: number;
      }) {
        dispatch({
          type: CONFIRM_PRESENCE_SET_METADADOS,
          payload: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            userAgent: window.navigator.userAgent,
          },
        });
      });
    }

    if (signatureType === undefined) {
      dispatch({
        type: CONFIRM_PRESENCE_SET_METADADOS,
        payload: {
          data: undefined,
          latitude: undefined,
          longitude: undefined,
          userAgent: undefined,
          imagem: undefined,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureType]);

  useEffect(() => {
    if (!isMobile() && devices.length > 1) {
      setFacingMode(FACING_MODE_ENVIRONMENT);
    }
  }, [devices]);

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  useEffect(() => {
    if (signatureType === SignatureTypes.FACIAL) {
      if (isSafari) {
        navigator.geolocation.getCurrentPosition(
          () => setAllowGeolocation(true),
          () => {
            setAllowGeolocation(false);
          }
        );
      } else {
        navigator.permissions
          .query({ name: 'geolocation' })
          .then(function (result) {
            if (result.state === 'granted') {
              setAllowGeolocation(true);
            } else if (result.state === 'denied') {
              setAllowGeolocation(false);
            }
          });
      }
    }
  }, [allowGeolocation, showToast, signatureType, state.metadados, isSafari]);

  const cpfForPresence = useMemo(() => {
    if (state.accountSelected.some((x: AccountListProps) => x.idConvocado)) {
      const value = state && state.accountSelected[0];

      if (state.isSearchByCpf) {
        if (
          !value.menorSemEmancipado &&
          value.cpfRepresentanteLegal === null &&
          !value.representantes.length
        ) {
          return value.cpfCnpj;
        }

        if (value.menorSemEmancipado) {
          if (value.representantes.length) {
            return (
              representative!?.cpf ?? state.filter.value!?.replace(/\D/g, '')!
            );
          }
        } else {
          if (value.cpfCnpj.length > 11) {
            return value.cpfRepresentanteLegal;
          }
          return value.cpfCnpj;
        }

        if (
          state.accountSelected.length > 1 &&
          state.accountSelected!?.some(
            (x: { menorSemEmancipado: boolean }) => x.menorSemEmancipado
          )
        ) {
          return state.filter.value!?.replace(/\D/g, '')!;
        } else {
          return state.filter.value!?.replace(/\D/g, '')!;
        }
      } else {
        if (value.representantes!?.length) {
          return representative!?.cpf;
        }
        return value.cpfCnpj;
      }
    } else {
      return undefined;
    }
  }, [state, representative]);

  const showError = useMemo(() => {
    if (
      state.accountSelected.length === 1 &&
      state.accountSelected[0].cpfCnpj.length >= 11
    ) {
      if (
        state.accountSelected[0].menorSemEmancipado &&
        !state.accountSelected[0].representantes.length
      ) {
        return true;
      }
    }

    return false;
  }, [state.accountSelected]);

  const disableSave = useMemo(() => {
    return (
      !(
        !showCam &&
        signatureType === SignatureTypes.FACIAL &&
        !state.metadados?.imagem
      ) &&
      (sent ||
        !state.accountSelected.length ||
        (!state.isSearchByCpf &&
          state.accountSelected[0]!?.representantes.length! &&
          !representative) ||
        !signatureType ||
        (state.accountSelected.length === 1 &&
          state.accountSelected[0].cpfCnpj.length > 11 &&
          !state.accountSelected[0].representantes.length))
    );
  }, [
    representative,
    sent,
    showCam,
    signatureType,
    state.accountSelected,
    state.isSearchByCpf,
    state.metadados?.imagem,
  ]);

  return (
    <Modal
      preventEscExit={props.preventEscExit || false}
      preventMaskExit={props.preventMaskExit || false}
      modalKey={props.modalKey}
      mobileOn={props.mobileOn}
      title={props.title}
      className={rootClassName}
      onClose={props.onClose}
      compenseColPaddingContent
      size={ModalSizes.Large}
      footer={
        !confirmation && !showCam
          ? [
              {
                theme: ButtonThemes.Secondary,
                leftIcon: <CircleTimes />,
                children: translation.ConfirmarPresencaModal.cancel,
                'aria-label': translation.ConfirmarPresencaModal.cancel,
                onClick: handleClose,
              },
              {
                theme: ButtonThemes.Primary,
                leftIcon:
                  !showCam &&
                  signatureType === SignatureTypes.FACIAL &&
                  !state.metadados?.imagem ? (
                    <CameraIcon />
                  ) : (
                    <UserIcon />
                  ),
                children:
                  !showCam &&
                  signatureType === SignatureTypes.FACIAL &&
                  !state.metadados?.imagem
                    ? translation.ConfirmarPresencaModal.captureImage
                    : translation.ConfirmarPresencaModal.confirm,
                'aria-label':
                  !showCam &&
                  signatureType === SignatureTypes.FACIAL &&
                  !state.metadados?.imagem
                    ? translation.ConfirmarPresencaModal.captureImage
                    : translation.ConfirmarPresencaModal.confirm,
                disabled: disableSave,
                loading: sent,
                onClick: () => {
                  if (
                    !showCam &&
                    signatureType === SignatureTypes.FACIAL &&
                    !state.metadados?.imagem
                  ) {
                    setShowCam(true);
                    setLoadCam(true);
                  } else {
                    return new Promise<any>((resolve: any) => {
                      if (!sent) setSent(true);

                      if (typeof props.onConfirm === 'function') {
                        postRegisterPresence(
                          signatureType === SignatureTypes.FISICA
                            ? {
                                convocacoes: state.accountSelected.map(
                                  (x: AccountListProps) => x.idConvocado
                                ),
                                cpf: cpfForPresence,
                                idEvento: Number(props.eventId),
                                tipoAssinatura: signatureType!,
                              }
                            : {
                                convocacoes: state.accountSelected.map(
                                  (x: AccountListProps) => x.idConvocado
                                ),
                                cpf: cpfForPresence,
                                idEvento: Number(props.eventId),
                                tipoAssinatura: signatureType!,
                                metadados: {
                                  latitude: state.metadados.latitude,
                                  longitude: state.metadados.longitude,
                                  data: state.metadados.data,
                                  userAgent: state.metadados.userAgent,
                                  imagem: state.metadados.imagem,
                                  ip: state.metadados.ip,
                                },
                              }
                        )
                          .then(() => {
                            setSent(false);
                            setConfirmation(true);
                            resolve();

                            if (
                              evento?.modo ===
                              AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO
                            ) {
                              setTimeout(() => {
                                dispatch({
                                  type: CONFIRM_PRESENCE_SET,
                                  payload: [],
                                });
                                dispatch({
                                  type: CONFIRM_PRESENCE_LIST,
                                  payload: [],
                                });
                                dispatch({
                                  type: CONFIRM_PRESENCE_CHANGE_INPUT,
                                  payload: [],
                                });
                                setError(false);
                                setAllPresent(false);
                                setErrorWithoutAccount(false);
                                setConfirmation(false);
                                setSignatureType(undefined);
                              }, 3000);
                            } else {
                              setTimeout(() => {
                                handleClose();
                              }, 3000);
                            }
                          })
                          .catch((err) => {
                            setSent(false);

                            showToast({
                              pauseOnFocusLoss: true,
                              theme: ToastTypes.Error,
                              showStatusBar: true,
                              timeout: 4000,
                              label:
                                err.response?.data.message.length >= 50
                                  ? err.response?.data.message
                                  : languageValues.toasts.error.keyErrors(
                                      err.response?.data.message
                                    ),
                            });
                          });
                      }
                    });
                  }
                },
              },
            ]
          : showCam
          ? !state.metadados?.imagem && isMobile()
            ? [
                {
                  theme: ButtonThemes.Secondary,
                  leftIcon: !state.metadados?.imagem ? (
                    <CircleTimes />
                  ) : (
                    <EditIcon />
                  ),
                  children: !state.metadados?.imagem
                    ? translation.ConfirmarPresencaModal.cancel
                    : translation.ConfirmarPresencaModal.captureAgain,
                  'aria-label': !state.metadados?.imagem
                    ? translation.ConfirmarPresencaModal.cancel
                    : translation.ConfirmarPresencaModal.captureAgain,
                  onClick: () => {
                    if (!state.metadados?.imagem) {
                      setSignatureType(SignatureTypes.FISICA);
                      setLoadCam(false);
                      setShowCam(false);
                    } else {
                      dispatch({
                        type: CONFIRM_PRESENCE_SET_METADADOS,
                        payload: {
                          data: undefined,
                          latitude: undefined,
                          longitude: undefined,
                          userAgent: undefined,
                          imagem: undefined,
                        },
                      });
                    }
                  },
                },
                {
                  theme: ButtonThemes.Secondary,
                  leftIcon: <RefreshIcon animate={false} />,
                  children: translation.ConfirmarPresencaModal.switchCam,
                  hidden: true,
                  'aria-label': translation.ConfirmarPresencaModal.switchCam,
                  onClick: () => handleSwitchCam(),
                },
                {
                  theme: ButtonThemes.Primary,
                  leftIcon: !state.metadados?.imagem ? (
                    <CameraIcon />
                  ) : (
                    <CheckCircleIcon />
                  ),
                  children: !state.metadados?.imagem
                    ? translation.ConfirmarPresencaModal.capturePhoto
                    : translation.ConfirmarPresencaModal.savePicture,
                  'aria-label': !state.metadados?.imagem
                    ? translation.ConfirmarPresencaModal.capturePhoto
                    : translation.ConfirmarPresencaModal.savePicture,
                  onClick: () => {
                    if (!state.metadados?.imagem) {
                      capture();
                      dispatch({
                        type: CONFIRM_PRESENCE_SET_METADADOS,
                        payload: {
                          data: moment(date).format('DD/MM/YYYY - HH:mm:ss'),
                        },
                      });
                    } else {
                      setSignatureType(SignatureTypes.FISICA);
                    }
                  },
                  disabled: loadCam,
                },
              ]
            : [
                {
                  theme: ButtonThemes.Secondary,
                  leftIcon: !state.metadados?.imagem ? (
                    <CircleTimes />
                  ) : (
                    <EditIcon />
                  ),
                  children: !state.metadados?.imagem
                    ? translation.ConfirmarPresencaModal.cancel
                    : translation.ConfirmarPresencaModal.captureAgain,
                  'aria-label': !state.metadados?.imagem
                    ? translation.ConfirmarPresencaModal.cancel
                    : translation.ConfirmarPresencaModal.captureAgain,
                  onClick: () => {
                    if (!state.metadados?.imagem) {
                      setShowCam(false);
                    } else {
                      dispatch({
                        type: CONFIRM_PRESENCE_SET_METADADOS,
                        payload: {
                          data: undefined,
                          ip: undefined,
                          latitude: undefined,
                          longitude: undefined,
                          userAgent: undefined,
                          imagem: undefined,
                        },
                      });
                    }
                  },
                },
                {
                  theme: ButtonThemes.Primary,
                  leftIcon: !state.metadados?.imagem ? (
                    <CameraIcon />
                  ) : (
                    <CheckCircleIcon />
                  ),
                  children: !state.metadados?.imagem
                    ? translation.ConfirmarPresencaModal.capturePhoto
                    : translation.ConfirmarPresencaModal.savePicture,
                  'aria-label': !state.metadados?.imagem
                    ? translation.ConfirmarPresencaModal.capturePhoto
                    : translation.ConfirmarPresencaModal.savePicture,
                  onClick: () => {
                    if (!state.metadados?.imagem) {
                      capture();
                      dispatch({
                        type: CONFIRM_PRESENCE_SET_METADADOS,
                        payload: {
                          data: moment(date).format('DD/MM/YYYY - HH:mm:ss'),
                        },
                      });
                    } else {
                      setShowCam(false);
                    }
                  },
                  disabled: loadCam,
                },
              ]
          : undefined
      }
    >
      <Row>
        <Collapse opened={!confirmation && !showCam} animateOpacity>
          <Row>
            <Col>
              <Input
                data-testid={'input-name-or-cpf'}
                value={state.filter.value}
                label={translation.ConfirmarPresencaModal.inputLabel}
                placeholder={
                  translation.ConfirmarPresencaModal.inputPlaceholder
                }
                onChange={handleChange}
                rightObject={
                  !state.accountLoading ? <SearchIcon /> : <RefreshIcon />
                }
                onKeyPress={(event) => {
                  if (is_numeric(state.filter.value)) {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }
                }}
                loading={state.accountLoading}
                error={
                  error && allPresent
                    ? true
                    : !error && errorWithoutAccount && !state.accountList.length
                    ? true
                    : error && state.filter.value && validator
                    ? is_numeric(state.filter.value)
                      ? validator.CpfIsValid().error
                      : validator?.nameIsValid().error
                    : false
                }
                helperText={
                  error && allPresent
                    ? 'Presença(s) já confirmada(s)'
                    : !error && errorWithoutAccount && !state.accountList.length
                    ? 'Dados não foram encontrados.'
                    : error && state.filter.value && validator
                    ? is_numeric(state.filter.value)
                      ? validator.CpfIsValid().helperText
                      : validator.nameIsValid().helperText
                    : ''
                }
              />
            </Col>
          </Row>
          <Collapse
            opened={Boolean(state.accountList!?.length) && !showCam}
            animateOpacity
          >
            {showError ? (
              <Row>
                <Col noPaddingTop>
                  <p className={'nunito'}>
                    {
                      translation.ConfirmarPresencaModal
                        .withoutLegalRepresentative
                    }
                  </p>
                </Col>
              </Row>
            ) : undefined}

            {state.accountList.length > 1 && !allPresent ? (
              <Row>
                <Collapse opened={state.isSearchByCpf} animateOpacity>
                  <Col>
                    <p className={'nunito'}>
                      {translation.ConfirmarPresencaModal.mostAccount}
                    </p>
                  </Col>
                </Collapse>
                <Col>
                  <b className={'nunito'}>
                    {translation.ConfirmarPresencaModal.selectAccounts}
                  </b>
                </Col>
                {state.isSearchByCpf && !allPresent ? (
                  <Col className={`${rootClassName}-select-all nunito`}>
                    <Checkbox
                      data-testid={`checked-all-account`}
                      value={Boolean(
                        state.accountList &&
                          state.accountList.length &&
                          state.accountList
                            .map((item: AccountListProps) => item)
                            .filter((x: AccountListProps) => !x.presente)
                            .length === selected.length
                      )}
                      onChange={handleSelectAll}
                    />
                    <label
                      onClick={() => {
                        setSelectAll((val) => !val);
                        selectAll
                          ? setSelected(
                              state.accountList
                                .map((item: AccountListProps) => item)
                                .filter((x: AccountListProps) => !x.presente)
                            )
                          : setSelected([]);
                      }}
                    >
                      {translation.ConfirmarPresencaModal.selectAllAccounts}
                    </label>
                  </Col>
                ) : undefined}
              </Row>
            ) : undefined}
            {state.accountList.map((item: AccountListProps, index: number) => {
              return (
                <Row className={rootClassName} key={index}>
                  <Col>
                    <div
                      className={`${rootClassName}-list-container ${
                        item.representantes!?.length ? 'fixed-position' : ''
                      }`}
                    >
                      {!item.presente && state.accountList.length > 1 ? (
                        state.isSearchByCpf ? (
                          <Checkbox
                            data-testid={`checked-list-${index}`}
                            value={selected.includes(item)}
                            onChange={(val: boolean) => {
                              if (val) {
                                setSelected(val ? [...selected, item!] : []);
                              } else {
                                setSelected(
                                  selected.filter(
                                    (s: AccountListProps) => s !== item!
                                  )
                                );
                              }
                            }}
                          />
                        ) : (
                          <RadioButton
                            data-testid={`radio-list-${index}`}
                            theme={RadioButtonType.New}
                            value={selected[0] === item}
                            onChange={(val) => {
                              if (
                                item.representantes &&
                                item.representantes.length
                              ) {
                                setExpand(val ? item.idConvocado : null);
                                setSelected(val ? [item] : []);
                              } else {
                                setSelected(val ? [item] : []);
                                setExpand(null);
                              }
                            }}
                          />
                        )
                      ) : undefined}
                      <Row
                        className={`${rootClassName}-box`}
                        data-testid={`list-box-${index}`}
                      >
                        <Col className={`${rootClassName}-box-header nunito`}>
                          <b>{item.nomeAssociado}</b>
                          <p>
                            {item.cpfCnpj.length > 11 ? (
                              <b>{translation.ConfirmarPresencaModal.cnpj}</b>
                            ) : (
                              <b>{translation.ConfirmarPresencaModal.cpf}</b>
                            )}
                            {formatCPFOrCNPJ(item.cpfCnpj)}
                          </p>
                        </Col>
                        <Col className={`${rootClassName}-box-footer nunito`}>
                          <p>
                            <b>{translation.ConfirmarPresencaModal.account}</b>{' '}
                            {item.numeroConta}
                          </p>
                          <p>
                            <b>{translation.ConfirmarPresencaModal.coop}</b>{' '}
                            {item.codigoCooperativa}
                          </p>
                          <p>
                            <b>{translation.ConfirmarPresencaModal.branch}</b>{' '}
                            {item.codigoAgencia}
                          </p>
                          <p>
                            <b>{translation.ConfirmarPresencaModal.nuc}</b>{' '}
                            {item.numeroNucleo}
                          </p>
                        </Col>
                        {item.presente ? (
                          <Col
                            className={`${rootClassName}-box-present nunito`}
                          >
                            <b>
                              {
                                translation.ConfirmarPresencaModal
                                  .confirmedPresence
                              }
                            </b>

                            <span>
                              <Etiqueta
                                icon={getModalityType(item.modoPresenca)}
                                label={getModalityDescription(
                                  item.modoPresenca
                                )}
                                theme={EtiquetasStyle.Finalizado}
                              />
                            </span>
                          </Col>
                        ) : undefined}
                        {item.representantes && item.representantes.length ? (
                          <Collapse
                            opened={
                              expand === item.idConvocado ||
                              (!item.presente &&
                                state.accountList &&
                                state.accountList!.length === 1)
                            }
                          >
                            <Row
                              className={`${rootClassName}-representantes nunito`}
                            >
                              <Col>
                                <p
                                  className={`${rootClassName}-representantes-title`}
                                >
                                  {
                                    translation.ConfirmarPresencaModal
                                      .selectLegalRepresentative
                                  }
                                </p>
                              </Col>
                              {item.representantes.map(
                                (
                                  x: { cpf: string; nome: string },
                                  index: number
                                ) => {
                                  return (
                                    <Row key={index}>
                                      <Col
                                        cols={[1, 1, 1, 1, 1]}
                                        className={`${rootClassName}-radio-content`}
                                      >
                                        <RadioButton
                                          data-testid={`radio-list-representative-${index}`}
                                          theme={RadioButtonType.New}
                                          value={
                                            JSON.stringify(representative) ===
                                            JSON.stringify(x)
                                          }
                                          onChange={(val) => {
                                            if (val) {
                                              setRepresentative(x);
                                            } else {
                                              setRepresentative(undefined);
                                            }
                                          }}
                                        />
                                      </Col>
                                      <div>
                                        <Col>
                                          <strong className={`nunito`}>
                                            {x.nome}
                                          </strong>
                                        </Col>
                                        <Col>
                                          <p className={`nunito`}>
                                            <strong className={`nunito`}>
                                              CPF:
                                            </strong>{' '}
                                            {formatCPF(x.cpf)}
                                          </p>
                                        </Col>
                                      </div>
                                    </Row>
                                  );
                                }
                              )}
                            </Row>
                          </Collapse>
                        ) : (
                          <Fragment />
                        )}
                      </Row>
                    </div>
                  </Col>
                </Row>
              );
            })}
            {showError ? (
              <Fragment />
            ) : state.accountList.length && !state.allPresent ? (
              <Row>
                <Col className={`${rootClassName}-presents`}>
                  <p>{translation.ConfirmarPresencaModal.confirmPresenceBy}</p>
                </Col>

                <Col
                  cols={[12, 4, 4, 4, 4]}
                  className={`${rootClassName}-radio-content`}
                >
                  <ControlLabel
                    control={
                      <RadioButton
                        data-testid={'radio-physical-signature'}
                        onChange={(val) => {
                          setSignatureType(
                            val ? SignatureTypes.FISICA : undefined
                          );
                          dispatch({
                            type: CONFIRM_PRESENCE_SET_METADADOS,
                            payload: {
                              data: undefined,
                              latitude: undefined,
                              longitude: undefined,
                              userAgent: undefined,
                              imagem: undefined,
                            },
                          });
                        }}
                        value={String(signatureType) === SignatureTypes.FISICA}
                        disabled={
                          state.accountSelected.length === 1 &&
                          state.accountSelected[0].cpfCnpj.length > 11 &&
                          !state.accountSelected[0].representantes.length
                        }
                      />
                    }
                    label={translation.ConfirmarPresencaModal.physicalSignature}
                  />
                </Col>
                {!['AGO', 'AGE', 'AOE'].includes(evento?.tipo || '') ? (
                  <Col
                    cols={[12, 4, 4, 4, 4]}
                    className={`${rootClassName}-radio-content`}
                  >
                    <ControlLabel
                      control={
                        <RadioButton
                          data-testid={'radio-biometric-signature'}
                          onChange={(val) => {
                            setSignatureType(
                              val ? SignatureTypes.FACIAL : undefined
                            );
                          }}
                          value={
                            String(signatureType) === SignatureTypes.FACIAL
                          }
                          disabled={
                            state.accountSelected.length === 1 &&
                            state.accountSelected[0].cpfCnpj.length > 11 &&
                            !state.accountSelected[0].representantes.length
                          }
                        />
                      }
                      label={translation.ConfirmarPresencaModal.faceSignature}
                    />
                  </Col>
                ) : undefined}
              </Row>
            ) : undefined}
          </Collapse>
        </Collapse>
        <Collapse opened={showCam} animateOpacity>
          <Row>
            <Col className={`${rootClassName}-video-container`} centralized>
              {loadCam ? (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <ThreeDotsLoader />
                </div>
              ) : undefined}
              {state.metadados?.imagem === undefined ? (
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat={'image/jpeg'}
                  videoConstraints={{
                    ...videoConstraints,
                    facingMode,
                  }}
                  onUserMedia={handleUserMedia}
                />
              ) : (
                <img
                  src={`data:image/jpeg;base64,${state.metadados?.imagem}`}
                  alt={'presence-img'}
                />
              )}
            </Col>
          </Row>
        </Collapse>
      </Row>
      <CSSTransition
        in={Boolean(confirmation)}
        timeout={500}
        classNames={'fade'}
        mountOnEnter
        unmountOnExit
      >
        <div className={`${rootClassName}-confirmation`}>
          <div className={`${rootClassName}-icon`}>
            <CheckCircleIcon />
          </div>
          <div className={`${rootClassName}-label`}>
            {translation.ConfirmarPresencaModal.confirmedPresence2}
          </div>
        </div>
      </CSSTransition>
    </Modal>
  );
};
